import { fmtMemberId } from '@lib/member-id';
import { clientLabel } from '@lib/client-key';
import { identifierSpaceLabel } from '@lib/token-space';
import type { Eligibility, MemberKit, TokenType } from '@/types/intake';

/**
 * What the technician entered, and which identifier space the server read it in. Absent for a
 * queue pick, where nothing was keyed and there is nothing to have misread.
 */
export interface ReadAs {
  token: string;
  tokenType: TokenType;
}

const VERDICT: Record<Eligibility, { label: string; cls: string }> = {
  eligible: { label: 'In queue - ready to result', cls: 'pill pill-success' },
  'override-needed': { label: 'Not yet shown as arrived - override needed', cls: 'pill pill-warning' },
  'already-resulted': { label: 'Already resulted - cannot submit', cls: 'pill pill-danger' },
  'result-conflict': {
    label: 'Stop: a result on file for this kit names another member - ask before going on',
    cls: 'pill pill-danger',
  },
  'not-found': { label: 'No matching kit', cls: 'pill' },
};

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="leader-row">
      <span className="k">{label}</span>
      <span className="leader" />
      <span className="v">{value || '-'}</span>
    </div>
  );
}

/** Member + kit + USPS trail snapshot shown before CONFIRM. */
export function ConfirmationCard({
  kit,
  eligibility,
  readAs,
}: {
  kit: MemberKit;
  eligibility: Eligibility;
  /**
   * The entered value and the identifier space it was read in. Shown so the technician can see
   * that a number they read off a kit was looked up as a member id, which is otherwise invisible:
   * the card below renders a name and a date of birth either way. See lib/token-space.ts.
   */
  readAs?: ReadAs | null;
}) {
  const v = VERDICT[eligibility];
  return (
    <div className="card">
      <div className="mb-3">
        <span className={v.cls}>{v.label}</span>
      </div>
      {readAs?.token ? (
        <div className="note mb-3" data-testid="read-as">
          Read <span className="li-mono">{readAs.token}</span> as a{' '}
          <b>{identifierSpaceLabel(readAs.tokenType)}</b>.
        </div>
      ) : null}
      <div className="mb-1 text-lg font-semibold text-cdx-fg-strong">{kit.memberName || '(no name on file)'}</div>
      <div className="mt-2">
        <Row label="Health plan" value={clientLabel(kit.client)} />
        <Row label="Member ID" value={fmtMemberId(kit.memberId)} />
        <Row label="DOB" value={kit.memberDob ?? ''} />
        <Row label="Kit barcode" value={kit.kitBarcode} />
        <Row label="Accession" value={kit.accessionBarcode} />
      </div>
      <hr className="rule" style={{ margin: '14px 0' }} />
      <div>
        <Row label="Delivered to member" value={kit.usps.deliveredToMemberTs ?? 'not scanned'} />
        <Row label="Received at lab" value={kit.usps.sampleReceivedTs ?? 'not scanned'} />
      </div>
    </div>
  );
}
